import {
  Breadcrumb,
  getTheme,
  IBreadcrumbItem,
  Icon,
  mergeStyleSets,
  Pivot,
  PivotItem,
  PrimaryButton,
  Stack,
  Text,
} from '@fluentui/react';
import * as React from 'react';
import { navigateTo } from '../../../src/hooks';
import { Question } from '../../Question';
import { MsCertifiedBadge } from '../../MsCertifiedBadge';

const PRODUCT_URL = 'https://reenhanced.com/products/power-form-7/';
const LOGO_URL = 'https://reenhanced.com/wp-content/uploads/2020/10/icon.png';
const SCREENSHOT_SETTINGS = 'https://reenhanced.com/wp-content/uploads/2020/10/pf7-settings.jpg';
const SCREENSHOT_TRIGGER = 'https://reenhanced.com/wp-content/uploads/2020/10/when-a-cf7-form-is-submitted.jpg';
const SCREENSHOT_SUBMIT = 'https://reenhanced.com/wp-content/uploads/2020/10/submit-a-cf7-form.jpg';

const theme = getTheme();

const style = mergeStyleSets({
  container: {},
  heroBanner: {
    background: `linear-gradient(135deg, ${theme.palette.themePrimary} 0%, ${theme.palette.themeDarker} 100%)`,
    borderRadius: 12, padding: '32px 36px', marginBottom: 28, color: '#fff',
  },
  heroLogoRow: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 },
  heroLogo: { width: 56, height: 56, borderRadius: 10, objectFit: 'cover' as const, flexShrink: 0 },
  heroTitle: { ...theme.fonts.xxLarge, color: '#fff', margin: 0, fontWeight: 700 },
  heroSubtitle: { ...theme.fonts.mediumPlus, color: 'rgba(255,255,255,0.88)', marginBottom: 20, lineHeight: '1.6' },
  heroPricing: { ...theme.fonts.small, color: 'rgba(255,255,255,0.7)', marginTop: 8, display: 'block' as const },
  trustRow: { display: 'flex', flexWrap: 'wrap' as const, gap: 10, marginBottom: 28 },
  trustPill: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    backgroundColor: theme.palette.neutralLighterAlt,
    border: `1px solid ${theme.palette.neutralLight}`,
    borderRadius: 20, padding: '5px 12px', fontSize: 13,
    color: theme.palette.neutralPrimary,
  },
  sectionTitle: { ...theme.fonts.xLarge, fontWeight: 700, marginBottom: 16, marginTop: 24 },
  featureGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14, marginBottom: 24 },
  featureCard: {
    backgroundColor: theme.palette.neutralLighterAlt,
    border: `1px solid ${theme.palette.neutralLight}`,
    borderRadius: 8, padding: '14px 16px',
  },
  featureIcon: { color: theme.palette.themePrimary, fontSize: 20, marginBottom: 6, display: 'block' as const },
  featureTitle: { ...theme.fonts.medium, fontWeight: 700, marginBottom: 4 },
  featureText: { ...theme.fonts.small, color: theme.palette.neutralSecondary, lineHeight: '1.5' },
  screenshot: {
    width: '100%', maxWidth: 560, borderRadius: 8,
    border: `1px solid ${theme.palette.neutralLight}`,
    display: 'block' as const, margin: '16px 0',
  },
  useCaseList: { listStyle: 'none', paddingLeft: 0, margin: 0 },
  useCaseItem: { display: 'flex', gap: 12, padding: '12px 0', borderBottom: `1px solid ${theme.palette.neutralLighter}` },
  useCaseIcon: { color: theme.palette.themePrimary, fontSize: 20, flexShrink: 0, marginTop: 2 },
  useCaseTitle: { ...theme.fonts.medium, fontWeight: 700, marginBottom: 2 },
  useCaseDesc: { ...theme.fonts.small, color: theme.palette.neutralSecondary },
  guaranteeBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    backgroundColor: '#e6f4ea', border: '1px solid #a8d5b0',
    borderRadius: 8, padding: '8px 16px', marginTop: 12,
  },
  stepList: { counterReset: 'step', listStyle: 'none', paddingLeft: 0 },
  stepItem: {
    display: 'flex', gap: 16, padding: '10px 0',
    borderBottom: `1px solid ${theme.palette.neutralLighter}`,
  },
  stepNum: {
    flexShrink: 0, width: 28, height: 28, borderRadius: '50%',
    backgroundColor: theme.palette.themePrimary, color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 700,
  },
  privacyBox: {
    backgroundColor: theme.palette.neutralLighterAlt,
    border: `1px solid ${theme.palette.neutralLight}`,
    borderRadius: 6, padding: '14px 20px', marginTop: 16,
  },
  highlight: { backgroundColor: 'rgba(255, 245, 100, 0.65)', padding: '0 4px', borderRadius: 2 },
});

const useCases = [
  { icon: 'ExcelLogo', title: 'Live Excel reports from form entries', desc: 'Every CF7 submission is appended to a shared Excel workbook automatically — no manual exports.' },
  { icon: 'SharepointLogo', title: 'Log entries into SharePoint', desc: 'Build a SharePoint list that fills up as your form is submitted — searchable, filterable, and always up to date.' },
  { icon: 'TeamsLogo', title: 'Teams alerts on new submissions', desc: 'Post a message to a Microsoft Teams channel every time a Contact Form 7 form is submitted.' },
  { icon: 'ContactCard', title: 'Sync leads to your CRM', desc: 'Push form submissions straight into Dynamics 365 or Salesforce, triggering automated follow-up sequences.' },
  { icon: 'Send', title: 'Automated marketing campaigns', desc: 'Add form submitters to Dynamics 365 Customer Insights journeys or any email-marketing flow the moment they submit.' },
  { icon: 'Workflow', title: 'Complex internal business processes', desc: 'Use form submissions as the input for multi-step internal workflows — approvals, notifications, file management, and more.' },
  { icon: 'PlugDisconnected', title: 'Two-way form integration', desc: 'Run the flow in reverse: use a Power Automate action to submit data back into a CF7 form from any connected service.' },
];

export const PowerForm7: React.FC = () => {
  const breadcrumbs: IBreadcrumbItem[] = [
    { text: 'Products', key: 'products', onClick: (ev) => { ev.preventDefault(); navigateTo('/products'); } },
    { text: 'Power Form 7', key: 'pf7' },
  ];

  return (
    <>
      <Breadcrumb items={breadcrumbs} />
      <div className={style.container}>

        {/* ── Hero ── */}
        <div className={style.heroBanner}>
          <div className={style.heroLogoRow}>
            <img src={LOGO_URL} alt="Power Form 7" className={style.heroLogo} />
            <h1 className={style.heroTitle}>Power Form 7</h1>
          </div>
          <p className={style.heroSubtitle}>
            The quickest and easiest way to connect <strong style={{ color: '#fff' }}>Contact Form 7</strong> to
            Microsoft Power Automate and over <strong style={{ color: '#fff' }}>1,000+ services</strong> — including
            Dynamics 365, Teams, Excel, and SharePoint. Trigger flows from any CF7 submission and get two-way
            data integration, all without writing a single line of code.
          </p>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 14 }}>
            <PrimaryButton
              text="Get Started — First Month Free"
              iconProps={{ iconName: 'ShoppingCart' }}
              onClick={() => { window.location.href = PRODUCT_URL; }}
              styles={{ root: { backgroundColor: '#fff', color: theme.palette.themePrimary, borderColor: '#fff' } }}
            />
            <MsCertifiedBadge style={{ filter: 'brightness(2)' }} />
          </Stack>
          <span className={style.heroPricing}>
            From $19 / month &nbsp;&bull;&nbsp; Free trial with code <strong style={{ color: '#fff' }}>onemonthfree</strong> &nbsp;&bull;&nbsp; 30-day money-back guarantee
          </span>
        </div>

        {/* ── Trust signals ── */}
        <div className={style.trustRow}>
          {[
            { icon: 'Certificate', text: 'Microsoft Certified' },
            { icon: 'Code', text: 'No Code Required' },
            { icon: 'Shield', text: '30-Day Money-Back Guarantee' },
            { icon: 'Lock', text: 'Form Data Never Leaves Your Site' },
            { icon: 'PlugConnected', text: 'Two-Way Integration' },
            { icon: 'DocumentManagement', text: 'File Upload Support' },
          ].map(t => (
            <span key={t.icon} className={style.trustPill}>
              <Icon iconName={t.icon} style={{ color: theme.palette.themePrimary }} />
              {t.text}
            </span>
          ))}
        </div>

        {/* ── Guarantee badge ── */}
        <div className={style.guaranteeBadge}>
          <Icon iconName="Shield" style={{ color: '#2e7d32', fontSize: 20 }} />
          <div>
            <Text block style={{ fontWeight: 700, color: '#1b5e20' }}>30-Day No-Questions-Asked Money-Back Guarantee</Text>
            <Text block style={{ fontSize: 12, color: '#2e7d32' }}>Unhappy for any reason? Contact us for a full refund.</Text>
          </div>
        </div>

        <Pivot>
          {/* ── Overview ── */}
          <PivotItem headerText="Overview">
            <Text block style={{ ...theme.fonts.mediumPlus as React.CSSProperties, lineHeight: '1.7', marginTop: 16, marginBottom: 20 }}>
              Power Form 7 is <span className={style.highlight}>easily the most useful Contact Form 7 plugin ever made.</span>{' '}
              When a CF7 form is submitted, your data is sent to a Power Automate trigger that passes it to
              any of 1,000+ connected services — no code required. You can also run the flow in reverse to
              submit data back into a CF7 form from any Power Automate action, enabling true two-way integration.
            </Text>

            <img src={SCREENSHOT_TRIGGER} alt="Contact Form 7 trigger in Power Automate" className={style.screenshot} />

            <div className={style.sectionTitle}>Why Power Form 7?</div>
            <div className={style.featureGrid}>
              {[
                { icon: 'LightningBolt', title: 'Instant Trigger on Submission', text: 'Every CF7 form submission becomes a Power Automate trigger with all field values mapped dynamically.' },
                { icon: 'Sync', title: 'Two-Way Integration', text: 'Not just form → flow. Use a Power Automate action to submit data into any CF7 form from any connected service.' },
                { icon: 'Lock', title: 'Privacy by Design', text: 'Form metadata passes through our gateway; actual submission data is sent directly to Power Automate — we never see it.' },
                { icon: 'Attach', title: 'File Upload Support', text: 'File attachments on CF7 forms are fully supported and passed along in your Power Automate flows.' },
                { icon: 'Unlimited', title: 'Unlimited Flows', text: 'Run as many flows per form as you need. No per-trigger or per-entry fees.' },
                { icon: 'PlugConnected', title: '1,000+ Services', text: 'Dynamics 365, Teams, Excel, SharePoint, Outlook, Salesforce, and hundreds more — all from a single plugin.' },
              ].map(f => (
                <div key={f.icon} className={style.featureCard}>
                  <Icon iconName={f.icon} className={style.featureIcon} />
                  <div className={style.featureTitle}>{f.title}</div>
                  <div className={style.featureText}>{f.text}</div>
                </div>
              ))}
            </div>

            <img src={SCREENSHOT_SUBMIT} alt="Submit a CF7 form from Power Automate" className={style.screenshot} />
          </PivotItem>

          {/* ── Use Cases ── */}
          <PivotItem headerText="Use Cases">
            <div className={style.sectionTitle} style={{ marginTop: 16 }}>What You Can Automate — No Code Required</div>
            <ul className={style.useCaseList}>
              {useCases.map((uc, i) => (
                <li key={i} className={style.useCaseItem}>
                  <Icon iconName={uc.icon} className={style.useCaseIcon} />
                  <div>
                    <div className={style.useCaseTitle}>{uc.title}</div>
                    <div className={style.useCaseDesc}>{uc.desc}</div>
                  </div>
                </li>
              ))}
            </ul>
          </PivotItem>

          {/* ── Setup ── */}
          <PivotItem headerText="Setup">
            <div className={style.sectionTitle} style={{ marginTop: 16 }}>Up and Running in Minutes</div>
            <img src={SCREENSHOT_SETTINGS} alt="Power Form 7 settings page" className={style.screenshot} />
            <ol className={style.stepList}>
              {[
                'Purchase and download the plugin as a .zip file',
                'Upload to WordPress via the built-in plugin installer',
                'Activate the plugin',
                'Go to Contact → Power Automate Settings',
                'Enter your license key and configure global settings',
                'Your forms are now available as triggers and actions in Power Automate',
              ].map((step, i) => (
                <li key={i} className={style.stepItem}>
                  <span className={style.stepNum}>{i + 1}</span>
                  <span style={{ paddingTop: 4 }}>{step}</span>
                </li>
              ))}
            </ol>
            <Text block style={{ marginTop: 12, fontSize: 13, color: theme.palette.neutralSecondary }}>
              <em>Already have the plugin from the WordPress directory? Skip straight to step 4.</em>
            </Text>

            <div className={style.privacyBox}>
              <Text variant="mediumPlus" block style={{ fontWeight: 'bold', marginBottom: 6 }}>
                <Icon iconName="PrivacyLogo" /> Privacy
              </Text>
              <Text block>
                Form metadata (field definitions) passes through our gateway server to configure Power Automate.
                The <strong>actual form submissions</strong> are sent directly to Power Automate — they never pass
                through our servers. Your data is completely private and we have zero visibility into it.
              </Text>
            </div>
          </PivotItem>

          {/* ── FAQ ── */}
          <PivotItem headerText="FAQ">
            <Question question="Do I need a Power Automate license?">
              <p>Flows run under the account that creates them, so the flow creator needs a Power Automate license (often included in Microsoft 365 business plans). Each flow processes unlimited submissions, but each form needs its own dedicated flow.</p>
            </Question>
            <Question question="Is there a free trial?">
              <p>Yes — use coupon code <code>onemonthfree</code> at checkout on the 1-month, 1-domain plan for a completely free trial month. Upgrade any time from the My Account page.</p>
            </Question>
            <Question question="Are file uploads supported?">
              <p>Yes — file attachments on CF7 forms are fully supported in the current version.</p>
            </Question>
            <Question question="Can I use this on an internal-network server?">
              <p>As long as Azure web servers can reach your site and your site can reach Power Automate, yes. Contact us if you're in a restrictive network environment.</p>
            </Question>
            <Question question="Does this work with other CF7 plugins?">
              <p>Yes — Power Form 7 works alongside any other Contact Form 7 plugin or add-on.</p>
            </Question>
            <Question question="What if I'm unhappy with the product?">
              <p>We offer a 30-day no-questions-asked money-back guarantee. Just contact us and we'll process a full refund.</p>
            </Question>
          </PivotItem>
        </Pivot>

        {/* ── Bottom CTA ── */}
        <div style={{
          backgroundColor: theme.palette.neutralLighterAlt,
          border: `1px solid ${theme.palette.neutralLight}`,
          borderRadius: 10, padding: '24px 28px', marginTop: 24,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap' as const, gap: 16,
        }}>
          <div>
            <Text block style={{ ...theme.fonts.large as React.CSSProperties, fontWeight: 700, marginBottom: 4 }}>
              Ready to automate Contact Form 7?
            </Text>
            <Text block style={{ color: theme.palette.neutralSecondary }}>
              From $19 / month &nbsp;&bull;&nbsp; Free trial with code <strong>onemonthfree</strong> &nbsp;&bull;&nbsp; 30-day money-back guarantee
            </Text>
          </div>
          <PrimaryButton
            text="Get Started — First Month Free"
            iconProps={{ iconName: 'ShoppingCart' }}
            onClick={() => { window.location.href = PRODUCT_URL; }}
          />
        </div>

      </div>
    </>
  );
};

export default PowerForm7;
